import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Button, Form, Input, Skeleton } from 'antd';
import { getString } from '@nackle/intl-tools';
import { COMPLETE_SESSION_MUTATION } from '../../gql/queries/ILT_QUERY';
import SessionView from './SessionView';
import useInterval from '../../hooks/useInterval';
import moment from 'moment';



const Sessions = ( { item, sessions, options, updateRegistration, iltRegistrationInfo, loading, forceLoading, isLocked } ) => {
    return item?.isIlt ?
        <div className='subitems sessions'>
            <div className='session-header'>
                <h3>{ getString( 'ilt.sessions.title' ) }</h3>
                <SessionCompletionCode sessions={ sessions } isLocked={ isLocked } registrationInfo={ iltRegistrationInfo } onSubmitCb={ updateRegistration } />
            </div>
            <SessionView
                data={ sessions } 
                isLocked={ isLocked }
                options={ { ...options, loadingCount: 3 } } 
                registrationInfo={ iltRegistrationInfo }
                updateRegistrationCb={ updateRegistration }
                loading={ loading || forceLoading }/>
        </div> : <></>;
};

const SessionCompletionCode = ( { sessions, registrationInfo, onSubmitCb, isLocked } ) => {

    const [ completeSessionMutation ] = useMutation( COMPLETE_SESSION_MUTATION, { onCompleted: onCompleteSessionMutationCompleted, onError: onCompleteSessionMutationCompleted } ); // { input: { iltID, sessionID, registrationID, completionCode } }

    const [ loading, setLoading ] = useState( false );
    const [ feedback, setFeedback ] = useState();
    const [ status, setStatus ] = useState();
    const [ disabled, setDisabled ] = useState( true );

    function checkDisabled() {
        if ( isLocked || !registrationInfo || registrationInfo.isRegistered == false || registrationInfo.isCompleted == true || !registrationInfo.sessionId ) {
            setDisabled( true );
            return;
        }

        const now = moment();

        const session = sessions?.find( s => s.id == registrationInfo.sessionId );
        const tooEarly = !session?.sessionStart.isBefore( now );
        const isCancelled = session?.isCancelled;

        setDisabled( !session || tooEarly || isCancelled );
    }

    useInterval( checkDisabled, 1000 * 60 ); // check every minute if its now available

    useMemo( () => {
        checkDisabled();
    }, [ registrationInfo, sessions ] );

    async function onSubmit( values ) {

        if ( !values?.code || values.code.length < 3 ) {return;}

        setFeedback( getString( 'ilt.code.validating' ) );
        setStatus( 'validating' );
        setLoading( true );

        completeSessionMutation( {
            variables: { 
                input: { 
                    iltID: registrationInfo.iltId, 
                    sessionID: registrationInfo.sessionId, 
                    registrationID: registrationInfo.registrationId, 
                    completionCode: values.code 
                } 
            } 
        } );
        
    }
    
    function onCompleteSessionMutationCompleted( data ) {

        setLoading( false );

        if ( !data?.createSessionCompletion ) {
            setStatus( 'error' );
            setFeedback( getString( 'ilt.code.error' ) );
        } else if ( data?.createSessionCompletion ) {
            setStatus( 'success' );
            setFeedback();
            onSubmitCb?.( data, false );
        }
    }

    useEffect( () => {
        if ( registrationInfo?.isCompleted ) {
            setStatus( 'success' );
        }
    }, [ registrationInfo ] );

    return <div className='completion-code'>
        { registrationInfo ?
         
            <Form
                name='comp-code'
                initialValues={ {
                    code: registrationInfo?.completionCode ?? undefined
                } }
                onFinish={ onSubmit }
                disabled={ disabled }
                autoComplete='off'
                >

                <Form.Item
                    name='code'
                    validateStatus={ status }
                    hasFeedback={ !!feedback }
                    suffix={ false }
                    help={ feedback }
                    >
                    
                    <AlphaNumericInput disabled={ loading || disabled } placeholder={ getString( 'ilt.completion.code.hint' ) } />
                </Form.Item>

                <Form.Item>
                    <Button className='action-button primary mini' htmlType='submit' disabled={ loading || disabled }>
                        { getString( 'form.submit' ) }
                    </Button>
                </Form.Item>

            </Form> 
            : <Skeleton.Input active /> }
    </div>;
};

const AlphaNumericInput = ( { value, onChange, placeholder, disabled } ) => {


    function handleChange( e ) {
        const { value: inputValue } = e.target;
        const reg = /^[a-zA-Z0-9]+$/;
        if ( reg.test( inputValue ) || inputValue === '' ) {
            onChange( inputValue.toUpperCase().substring( 0, 10 ) );
        }
    }

    return <Input
        name='alphanum'
        disabled={ disabled }
        value={ value }
        onChange={ handleChange }
        placeholder={ placeholder }
        />;
};


export const getFormattedSession = ( session, course, locale ) => {

    function getAddressUrl( address ) {
        const link = 'https://www.google.com/maps/dir/?api=1&destination=';
        const addressArr = [ address.addressLine1, address.addressLine2, address.administrativeArea, address.locality, address.country?.name, address.postalCode ];
        return link.concat( addressArr.filter( a => !!a ).join( ', ' ).split( ' ' ).map( u => encodeURIComponent( u ) ).join( '+' ) );
    }

    function getAddressDetails( session ) {
        const address = session.address;


        if ( session.meetingUrl ) {
            return {
                locationLabel: getString( 'ilt.sessions.location.virtual.default' ),
                locationString: getString( 'ilt.sessions.location.virtual.default' ),
                locationLink: session.meetingUrl,
            };
        }

        const addressArr = [ 
            address.name,
            address.addressLine1, 
            address.addressLine2, 
            `${ address.locality }, ${ address.administrativeArea } ${ address.postalCode }`
        ].filter( a => a?.trim().length > 0 ).map( a => a.trim() );

        return {
            locationLabel: <>
                { addressArr.map( ( a, i ) => <span className='address-line' key={ `line-${ i }` }>{ a }</span> ) }
            </>,
            locationString: addressArr.join( ', ' ),
            locationLink: getAddressUrl( session.address )
        };
    }

    return {
        id: session.id,
        name: ( session.locales.find( l => l.locale == locale ) ?? session.locales.find( l => locale == 'en-US' ) ?? session.locales[ 0 ] ).name,
        iltId: session.instructorLedTrainingId,
        maxSeats: session.maxSeats,
        filledSeats: session.filledSeats,
        courseName: course.name,
        courseDescription: course.description,
        isCancelled: session.status != 'ACTIVE',
        isVirtual: !!session.meetingUrl,
        registrationStart: session.registrationOpenAt ? moment( session.registrationOpenAt ) : null,
        registrationEnd: session.registrationCloseAt ? moment( session.registrationCloseAt ) : null,
        sessionStart: moment( session.sessionStartAt ),
        sessionEnd: moment( session.sessionEndAt ),
        instructors: session.instructors,
        registrationUrl: session.registrationUrl,
        ...getAddressDetails( session ),
    };
};

export default Sessions;